
import React, { useCallback, useRef, useState } from 'react';
import {
    Button, Media, Card, CardBody, CardTitle, CardSubtitle,
    Form,
    FormGroup,
    Label,
    Spinner,
} from 'reactstrap';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import styled from 'styled-components';
import { Slate, withReact, useSlate } from 'slate-react';
import { withHistory } from 'slate-history';
import {
    EditablePlugins,
    pipe,
    BoldPlugin,
    ItalicPlugin,
    StrikethroughPlugin,
    UnderlinePlugin,
    AlignPlugin,
    SoftBreakPlugin
} from '@udecode/slate-plugins'
import { createEditor } from 'slate';

import {
    useSignState, useSign, OPEN_COMMENTS, SET_SIGNATURE_CONFIRMATIONS, CHANGE_ELEMENT_VERSION,
} from 'context/signview';
import { fetcher } from 'api/client';
import { useConstant } from 'hooks/useConstant';

import { clientContractAction } from 'api/actions/contracts';
import { parseDate } from 'utils';
import { createMarkup } from '../SignView';
import { serializeHTML, TratoFormat, IndividualSignatureElement, LegalSignatureElement } from '../../Editor/Canvas/TratoFormat';
import { getNodesById } from '../../Editor/Canvas/TratoFormat/editor-utils';
import { VersionLight } from '../sidebar/negotiation';

function NegotiationButtons({ element = {}, setShowNegotiationBox, showNegotiationBox }) {

    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const { contractId, jwt, contract, profile } = useSignState();
    const currentVersion = element.versions?.find(e => e._id === element.currentVersionId) || {};

    const { mutate: negotiateVersionMutation } = useMutation((payload) => fetcher(clientContractAction(contractId, 'versions', payload), jwt), {
        onSuccess: () => {
            queryClient.invalidateQueries('client-contract')
        }
    })

    const handleAcceptVersion = (evt) => {
        evt.stopPropagation();

        negotiateVersionMutation({ accept: true, elementId: element._id, currentVersionId: element.currentVersionId });
    }

    const handleCreateVersion = (evt) => {
        evt.stopPropagation();
        setShowNegotiationBox(true);
    }

    if (!element.isNegotiable) return null;

    if (showNegotiationBox) return null;

    if (!contract.signViewOptions?.showNegotiation) return null;

    return (
        !currentVersion.accepted?.includes(profile._id) &&
        !currentVersion.rejected?.includes(profile._id)
    )
        ?
        (
            <div className="d-block mb-3 mt-1 text-right">
                <Button color="primary" onClick={handleAcceptVersion}>
                    {t('sign.contract.accept')}{' '}
                    <i className="fas fa-thumbs-up lead-icon"></i>
                </Button>
                <Button color="secondary" className="ml-1" onClick={handleCreateVersion}>
                    {t('sign.contract.negotiation_create_version')}{' '}
                    <i className="fas fa-plus-circle lead-icon"></i>
                </Button>
            </div>
        ) :
        null

}

const plugins = [
    BoldPlugin(),
    ItalicPlugin(),
    StrikethroughPlugin(),
    UnderlinePlugin(),
    AlignPlugin(),
    SoftBreakPlugin({
        rules: [
            {
                hotkey: 'enter',
                query: {
                    allow: ['p'],
                }
            },
        ],
    }),
]

function MiniEditor({ className, initialData, onChange, minHeight = '100px' }) {
    const [value, setValue] = useState(initialData)
    const editor = useConstant(() => pipe(createEditor(), withReact, withHistory))

    const handleChange = (nodes) => {
        setValue(nodes)
        onChange(nodes)
    }

    return (
        <Slate editor={editor} value={value} onChange={handleChange}>
            <EditablePlugins
                style={{ minHeight }}
                className={className}
                plugins={plugins}
            />
        </Slate>
    )
}

function Versions({ element, participants = [], selectedVersion, onSelectVersion, signViewOptions }) {
    const versionsLength = element.versions?.length;

    const { t } = useTranslation();

    if (versionsLength <= 1) return null;

    const versions = element.versions || [];

    const handleSelectedVersion = (e) => {
        e.stopPropagation();
        const value = e.target.value;
        const version = versions?.find(v => v._id === value);

        onSelectVersion?.({
            value,
            index: versions.map(v => v._id).indexOf(value)
        }, version)
    }

    if (!signViewOptions?.showNegotiation) return null;

    return (<>
        <Form inline className="mb-2">
            <FormGroup className="mb-2 mr-sm-2 mb-sm-0">
                <Label for="version" className="mr-sm-2">{t('sign.contract.negotiation_current_version')}:</Label>
                <select
                    className="form-control"
                    onChange={handleSelectedVersion}
                    defaultValue={selectedVersion.value}>
                    {versions.map((v, index) => (
                        <option
                            key={index}
                            value={v._id}>
                            {index ? `${v.number} ${v.user} (${parseDate(v.createdAt, 'P')})` : t('sign.contract.negotiation_original')}
                        </option>
                    ))}
                </select>
                <Label className="ml-2">({selectedVersion.index + 1}/{versions.length})</Label>
            </FormGroup>
        </Form>
        <div className="d-flex mb-2">
            {participants.filter(p => !p.isNotNegotiator).map((p) => {
                const selectedV = versions?.find((v) => v._id === selectedVersion.value);
                if (!selectedV) return null;
                return <VersionLight participant={p} version={selectedV} key={p._id} />
            })}
        </div>
    </>)
}

function NegotiationBox({ setShowNegotiationBox, content, elementId, element, showNegotiationBox }) {

    const negotiationContent = useRef();

    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [elementChildren, setElementChildren] = useState();

    const { contractId, jwt } = useSignState();

    const handleCancelShowNegotiation = useCallback((evt) => {
        evt.stopPropagation();
        setShowNegotiationBox(false);
    }, [setShowNegotiationBox]);

    const { mutate: negotiateVersionMutation, isLoading } = useMutation((payload) => fetcher(clientContractAction(contractId, 'versions', payload), jwt), {
        onSuccess: () => {
            queryClient.invalidateQueries('client-contract');
            setShowNegotiationBox(false);

        }
    })

    const handleCreateVersion = (evt) => {
        evt.stopPropagation();
        negotiateVersionMutation({
            reject: true,
            elementId,
            children: elementChildren,
            viewedVersions: element.versions.map(v => v._id)
        });
    }
    const handleEditorChange = (nodes) => {
        setElementChildren(nodes[0].children)
    }

    if (!showNegotiationBox) return null;

    return (
        <Card className="animated fadeIn p-3">
            <CardBody >
                <CardTitle tag="h5"><b>{t('sign.negotiation.create_a_new_version')}</b></CardTitle>
                <CardSubtitle className="mb-2">
                    <MiniEditor className="negotiation-content p-2 border" initialData={[{ type: 'p', children: element.children }]} onChange={handleEditorChange} />
                </CardSubtitle>
                <div className="text-right">
                    <Button disabled={isLoading} color="light ml-2" onClick={handleCancelShowNegotiation}>{t('common.cancel')}</Button>
                    <Button disabled={isLoading} color="primary" onClick={handleCreateVersion}>
                        {isLoading && <Spinner size="sm" />}
                        {t('sign.contract.negotiation_create_version')}
                    </Button>
                </div>
            </CardBody>
        </Card>
    )
}

function CheckSign({ element, signCheckConfirmation, showSign }) {

    const [signState, dispatch] = useSign();

    const handleCheck = useCallback(() => {
        dispatch({ type: SET_SIGNATURE_CONFIRMATIONS, signatureConfirmation: element._id })
    }, [dispatch, element._id]);


    if (!signCheckConfirmation || element.type !== 'signature' || !showSign) return null;

    return (
        <div className="check-sign-class" onClick={handleCheck} id={`signature-check-${element._id}`}>
            <input
                id={`signature-check-input-${element._id}`}
                className="signature-checkbox"
                type="checkbox"
                checked={signState.signatureConfirmations[element._id]}
                readOnly={true}
            />
            <label htmlFor="_checkbox0" className={clsx(signState.focused && 'checkbox-highlighted')}>
                <div id="tick_mark"></div>
            </label>
        </div>
    )
}

function CommentLine() {
    return (
        <div className="select-line" style={{ minWidth: '10px', height: '100px' }}>
            <div className="dot-top"></div>
            <div className="dot-bottom"> </div>
            <div className="line-center">
                <div className="dot-end "></div>
            </div>
        </div>
    )
}

function SmartClause() {
    // TODO
    return null;
}

function NegotiationSideBar({ signViewOptions, element }) {

    if (!signViewOptions?.showNegotiation) return null

    return (
        <Media className="align-self-center" style={{ position: 'absolute', marginLeft: '-15px', zIndex: 3 }}>
            {['text', 'clause', 'statement'].includes(element.type) &&
                element.isAccepted ?
                <i className="fas fa-check-circle fa-2x"></i> :
                element.isNegotiable ?
                    <i className="fas fa-info-circle fa-2x text-primary"></i> :
                    <i className="fas fa-lock-circle fa-2x text-secondary"></i>
            }
        </Media>
    )
}

function Element({ element }) {

    // const editor = useMemo(() => withReact(createEditor()), [])
    const [{ contract, commentElementId, profile }, dispatch] = useSign();

    const signViewOptions = contract.signViewOptions || {};

    const [showNegotiationBox, setShowNegotiationBox] = useState(false);

    const handleComment = useCallback(() => {
        if (element.type !== 'signature' && !profile?.tenant?.config?.hideSignComments) {
            dispatch({ type: OPEN_COMMENTS, open: true, commentElementId: element._id, contractId: contract._id })
        }
    }, [contract._id, dispatch, element._id, element.type]);

    const versionsLength = element.versions.length;

    const [selectedVersion, setSelectedVersion] = useState({
        value: element.versions?.[versionsLength - 1]?._id,
        index: versionsLength - 1
    });

    const renderSignature = () => {
        return <>
          <div className="signatures-content">
              {contract.participants?.map(participant => {
                if (!element.content.includes(participant._id)) return null;
                const Component = participant.obligation === 'legal' ? LegalSignatureElement : IndividualSignatureElement
                return (
                  <Component 
                      key={participant._id}
                      participant={participant}
                      participantId={participant._id}
                      jurisdiction={contract.jurisdiction ?? 'mx'}
                      showSignatureBorder={false}
                  />
                )
              })}
              {/* <div dangerouslySetInnerHTML={{__html: content }} />  */}
          </div> 
        </>
    }

    return (
        <Media
            className={clsx('mb-1 pointer', {
                'bg-light': commentElementId === element._id,
                'border-right border-secondary': element.signComments?.length
            })}
        >
            <NegotiationSideBar
                signViewOptions={signViewOptions}
                element={element} />

            <Media body>
                <div className={clsx('p-4 mt-2 rounded-sm', (contract.signViewOptions?.showNegotiation && element.isNegotiable && 'bg-light border'))}>
                    <SmartClause />
                    <Versions
                        signViewOptions={contract.signViewOptions}
                        element={element}
                        participants={contract.participants}
                        selectedVersion={selectedVersion}
                        setSelectedVersion={setSelectedVersion}
                    />
                    <NegotiationButtons
                        element={element}
                        showNegotiationBox={showNegotiationBox}
                        setShowNegotiationBox={setShowNegotiationBox}
                    />
                    <CheckSign
                        element={element}
                        signCheckConfirmation={contract.signCheckConfirmation}
                        showSign={signViewOptions?.showSign}
                    />
                    {
                      element.type === 'signature' ? renderSignature() 
                      : <div
                            className={clsx(element.type === 'title' && 'text-center font-weight-bold')}
                            onClick={handleComment}
                            dangerouslySetInnerHTML={createMarkup(element.versions?.find(v => v._id === selectedVersion?.value)?.content || (contract.isMigrated && element.children ? serializeHTML({ nodes: element.children, contract }) : element.content))}
                       /> 
                      
                    }
                    

                </div>
                <NegotiationBox
                    element={element}
                    content={element.versions?.find(v => v._id === element.currentVersion)?.content || element.content}
                    setShowNegotiationBox={setShowNegotiationBox}
                    elementId={element._id}
                    showNegotiationBox={showNegotiationBox}
                />
            </Media>
            {/* <CommentLine /> */}
        </Media>
    )
}

function SignViewBaseElement({ element, children }) {

    // const editor = useMemo(() => withReact(createEditor()), [])
    const editor = useSlate();
    const [{ contract, commentElementId, profile }, dispatch] = useSign();

    const signViewOptions = contract.signViewOptions || {};

    const [showNegotiationBox, setShowNegotiationBox] = useState(false);

    const handleComment = useCallback(() => {
        if (element.type !== 'signature') {
            dispatch({ type: OPEN_COMMENTS, open: true, commentElementId: element._id, contractId: contract._id })
        }
    }, [contract._id, dispatch, element._id, element.type]);

    const versionsLength = element.versions?.length;

    const [selectedVersion, setSelectedVersion] = useState({
        value: element.currentVersionId || element.versions?.[versionsLength - 1]?._id,
        index: element.currentVersionIndex || versionsLength - 1
    });

    const handleSelectVersion = (selectedVersion, version) => {

        const [node, path] = getNodesById(editor, element._id)[0]
        // Transforms.insertNodes(editor, version.children, { at: path })
        dispatch({ type: CHANGE_ELEMENT_VERSION, elementPath: path, version })

        setSelectedVersion(selectedVersion)
    }

    const handleClick = useCallback(() => {
        if (element.type !== 'signature' && !showNegotiationBox && !profile?.tenant?.config?.hideSignComments) {
            dispatch({ type: OPEN_COMMENTS, open: true, commentElementId: element._id, contractId: contract._id })
        }
    }, [contract._id, dispatch, showNegotiationBox]);

    return (
        <Media
            className={clsx('mb-1 pointer position-relative', {
                'px-5': element.type !== 'break',
                'text-white border-bottom-dashed': element.type === 'break',
                'bg-light': commentElementId === element._id && element.type !== 'break',
                'border-right border-secondary': element.signComments?.length
            })}
            onClick={handleClick}
        >
            <NegotiationSideBar
                signViewOptions={signViewOptions}
                element={element} />

            <Media body>
                <div className={clsx('mt-2 rounded-sm', !['signature', 'break'].includes(element.type) && 'p-4', (contract.signViewOptions?.showNegotiation && element.isNegotiable && 'bg-light border'))}>
                    <SmartClause />
                    <Versions
                        signViewOptions={contract.signViewOptions}
                        element={element}
                        participants={contract.participants}
                        selectedVersion={selectedVersion}
                        onSelectVersion={handleSelectVersion}
                    />
                    <NegotiationButtons
                        element={element}
                        showNegotiationBox={showNegotiationBox}
                        setShowNegotiationBox={setShowNegotiationBox}
                    />
                    {children}

                </div>
                <NegotiationBox
                    element={element}
                    nodes={element.versions?.find(v => v._id === element.currentVersionId)?.children || element.children}
                    setShowNegotiationBox={setShowNegotiationBox}
                    elementId={element._id}
                    showNegotiationBox={showNegotiationBox}
                />
            </Media>
            {/* <CommentLine /> */}
        </Media>
    )
}

const HtmlPreviewRoot = styled.div`
    height: calc(100vh - 198px);
    @media (max-width: 576px) {
        .trato-format {
            zoom: 0.4
        }
    }
`

export default function HTMLPreview() {

    const [{ contract, signatureConfirmations }, dispatch] = useSign();

    const handleConfirmSignature = useCallback((participant, element) => {
        dispatch({ type: SET_SIGNATURE_CONFIRMATIONS, signatureConfirmation: element._id })
    }, [dispatch]);

    if (contract.isMigrated) {
        return (
            <HtmlPreviewRoot className="overflow-x-hidden bg-white">
                <TratoFormat
                    document={contract}
                    className="trato-format"
                    isEditable={false}
                    currentParticipantId={contract.signViewOptions?.currentParticipant?._id}
                    showSignatureConfirmation={contract.signViewOptions?.showSign && contract.signCheckConfirmation}
                    signatureConfirmations={signatureConfirmations}
                    BaseElementComponent={SignViewBaseElement}
                    showSignatureBorder={false}
                    showRemoveSignature={false}
                    showVariableHighlight={false}
                    showPlaceholder={false}
                    onConfirmSignature={handleConfirmSignature}
                />
            </HtmlPreviewRoot>

        )
    }

    return (
        <HtmlPreviewRoot className="p-5 bg-white overflow-y-auto">

            {contract.elements?.map((element) => (
                <Element
                    key={element._id}
                    element={element}
                />))}
        </HtmlPreviewRoot>
    )
}
